import type { printOnDemand } from "common/utils/models";
import type { SizeForm } from "@design-lab/shared/types";
import { DEFAULT_ARTWORK_THUMBNAIL_OPTIONS } from "common/constants/artwork-imgix";
import { artworkImgixThumbnailUrl } from "common/utils/remote-image";

type GeneratePreviewOptions = {
  imageUrl: string;
  artworkSize?: SizeForm | undefined;
  surface: printOnDemand.getProduct.SurfacesBack;
};

function artworkUrlForPreview(imageUrl: string): string {
  if (!imageUrl || typeof imageUrl !== "string") return imageUrl;
  if (imageUrl.startsWith("data:") || imageUrl.startsWith("blob:")) return imageUrl;
  return artworkImgixThumbnailUrl(imageUrl, DEFAULT_ARTWORK_THUMBNAIL_OPTIONS) ?? imageUrl;
}

type RenderImageOptions = Omit<GeneratePreviewOptions, "imageUrl"> & {
  canvas: HTMLCanvasElement;
  surfaceImage: HTMLImageElement;
  fileImage: HTMLImageElement;
};

function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new window.Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = url;
  });
}

function renderImage(options: RenderImageOptions) {
  const { canvas, surfaceImage, fileImage, artworkSize, surface } = options;
  const ctx = canvas.getContext("2d");
  if (!ctx) return;

  // Clear canvas
  ctx.clearRect(0, 0, canvas.width, canvas.height);

  // Draw surface image to fill the canvas
  ctx.drawImage(surfaceImage, 0, 0, canvas.width, canvas.height);

  // Calculate bounding box for artwork (all in px)
  const { left, top, width, height } = surface.boundingBox;
  const boxLeft = left;
  const boxTop = top;
  const boxWidth = width;
  const boxHeight = height;

  // Get image's natural size
  const imgNaturalWidth = fileImage.naturalWidth;
  const imgNaturalHeight = fileImage.naturalHeight;
  let maxWidthPx: number;
  let maxHeightPx: number;

  if (artworkSize && surface.ppi) {
    maxWidthPx = artworkSize.width * surface.ppi;
    maxHeightPx = artworkSize.height * surface.ppi;
  } else {
    maxWidthPx = imgNaturalWidth;
    maxHeightPx = imgNaturalHeight;
  }

  // First, fit image inside artworkSize box (if provided), preserving aspect ratio
  const scaleToArtwork = Math.min(maxWidthPx / imgNaturalWidth, maxHeightPx / imgNaturalHeight);
  let drawWidth = imgNaturalWidth * scaleToArtwork;
  let drawHeight = imgNaturalHeight * scaleToArtwork;

  // Then, if the result is larger than the bounding box, scale down again to fit bounding box
  const widthScale = boxWidth / drawWidth;
  const heightScale = boxHeight / drawHeight;
  const scaleToBox = Math.min(widthScale, heightScale, 1);
  drawWidth *= scaleToBox;
  drawHeight *= scaleToBox;

  // Center artwork in bounding box horizontally, anchor to top vertically
  const drawLeft = boxLeft + (boxWidth - drawWidth) / 2;
  const drawTop = boxTop;

  // Draw artwork image
  ctx.drawImage(fileImage, drawLeft, drawTop, drawWidth, drawHeight);
}

export async function generatePreview(
  options: GeneratePreviewOptions
): Promise<{ blob: Blob; base64: string }> {
  const { imageUrl, artworkSize, surface } = options;

  const artworkLoadUrl = artworkUrlForPreview(imageUrl);

  const [surfaceImage, fileImage] = await Promise.all([
    loadImage(surface.picture),
    loadImage(artworkLoadUrl)
  ]);

  // Determine canvas size
  const canvas = document.createElement("canvas");
  canvas.width = surfaceImage.naturalWidth;
  canvas.height = surfaceImage.naturalHeight;

  renderImage({
    canvas,
    surfaceImage,
    fileImage,
    artworkSize,
    surface
  });

  const base64 = canvas.toDataURL("image/png");
  const blob: Blob = await new Promise((resolve, reject) => {
    canvas.toBlob(canvasBlob => {
      if (canvasBlob) {
        resolve(canvasBlob);
      } else {
        reject(new Error("Failed to generate preview"));
      }
    }, "image/png");
  });

  // Clean up
  canvas.width = 0;
  canvas.height = 0;

  return { blob, base64 };
}
